import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import get from 'lodash.get';
import { AbsoluteBox, Box, InputField, Typography, Grid, color, textSize } from '@kis-technologies/kis-ui';
import { LoadingOverlay } from 'components/LoadingOverlay';
import { NumPadAction } from 'components/NumPad';
import { OPERATIONAL, UNKNOWN } from 'constants/hardware';
import { useActions } from 'hooks/useActions';
import { selectors as authSelectors, actions as authActions } from 'services/auth';
import { selectors as configSelectors, Configuration, Messages } from 'services/configuration';
import { selectors as hardwareSelectors, actions as hardwareActions, Scanner } from 'services/hardware';
import { selectors as ticketingSelectors } from 'services/ticketing';
import { useStyles } from './styled';

const PromoCodeApprovalView: React.FC = () => {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();

  const { adminPinValidation, resetAdminPinMessage, resetAdminPinAuthorized } = useActions({
    adminPinValidation: authActions.adminPinValidation,
    resetAdminPinMessage: authActions.resetAdminPinMessage,
    resetAdminPinAuthorized: authActions.resetAdminPinAuthorized
  });
  const { scannerRead, resetScanner } = useActions(hardwareActions);

  const adminPinAuthorized = useSelector(authSelectors.selectAdminPinAuthorized);
  const adminPinError = useSelector(authSelectors.selectAdminPinError);
  const isLoading = useSelector(authSelectors.selectLoading);
  const configuration = useSelector(configSelectors.selectConfiguration) as Configuration;
  const { status, loading: scannerLoading }: Scanner = useSelector(hardwareSelectors.selectScanner);
  const ticketingPath = useSelector(ticketingSelectors.selectTicketingConfigPath);

  const isPreviewMode = sessionStorage.getItem('mode') === 'preview';
  const messages: Messages = get(configuration, `${ticketingPath}.promoCode.selection.attendantApprovalMessages`, {});
  const headerText = get(messages, `header.${i18n.language}`, t('promoCode.approvalHeader'));
  const backButtonText = get(messages, `buttons.${i18n.language}[0]`, t('commonText.back'));
  const nextButtonText = get(messages, `buttons.${i18n.language}[1]`, t('commonText.next'));

  const [pinCode, setPinCode] = useState('');
  const [pageInitialized, setPageInitialized] = useState(false);

  const classes = useStyles();

  /**
   * Start reading input from the scanner if this criteria is met:
   * - Page has been initialized
   * - Not in preview mode
   * - Scanner status is not UNKNOWN (i.e. scanner is connected)
   * - Scanner is not already loading
   */
  const startScannerIfApplicable = () => {
    if (pageInitialized && !isPreviewMode && status && status.code !== UNKNOWN && !scannerLoading) {
      scannerRead();
    }
  };

  // Reset the state to initialize the page on component mount
  useEffect(() => {
    resetScanner();
    resetAdminPinAuthorized();
    resetAdminPinMessage();
    setTimeout(() => setPageInitialized(true), 0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Start scanner when page is initialized
  useEffect(() => {
    if (pageInitialized) startScannerIfApplicable();
  }, [pageInitialized]);

  // Handle scanner input
  useEffect(() => {
    if (!pageInitialized) return;

    if (status.message && status.code === OPERATIONAL && !isLoading) {
      if (adminPinError) resetAdminPinMessage();
      // Submit the scanned value for PIN validation
      const scannedPin = status.message.trim();
      setPinCode(scannedPin);
      adminPinValidation({ pinCode: scannedPin });
      resetScanner();
    } else if (status.rawCode === 'scanner_timeout') {
      // Trigger the scanner to read again
      resetScanner();
      startScannerIfApplicable();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status.message]);

  // Navigate to next page if authorized
  useEffect(() => {
    if (!pageInitialized) return;

    if (adminPinAuthorized) navigate('/ticketing/promo-code/selection');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [adminPinAuthorized]);

  // Restart scanner if there is an error
  useEffect(() => {
    if (!pageInitialized) return;

    if (adminPinError) startScannerIfApplicable();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [adminPinError]);

  // Reset error when the input changes
  useEffect(() => {
    if (adminPinError) resetAdminPinMessage();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pinCode]);

  const handleKeyboardClick = (_: React.MouseEvent<HTMLButtonElement>, v: string) => {
    if (v === 'delete') {
      setPinCode((prevValue) => prevValue.slice(0, -1));
    } else if (v === 'clear') {
      setPinCode('');
    } else {
      setPinCode((prevValue) => prevValue + v);
    }
  };

  // Navigate back
  const onPrevPageClick = () => {
    if (adminPinError) resetAdminPinMessage();
    navigate('/ticketing/promo-code/selection');
  };

  // Submit PIN for validation
  const onNextPageClick = () => {
    if (isLoading) return;
    adminPinValidation({ pinCode });
  };

  if (isLoading) return <LoadingOverlay style={{ zIndex: '99' }} />;

  return (
    <>
      <Grid className={classes.outerContainer}>
        <Grid className={classes.lookupViewWrapper}>
          <Box flexOverride="none" margin="6">
            <Typography fontSize={72} fontWeight={800} align="center">
              {headerText}
            </Typography>
          </Box>
          <Grid className={classes.inputContainer}>
            <InputField
              readOnly
              id="pin-code-input"
              data-testid="pin-code-input"
              autoFocus
              size="large"
              align="center"
              fullWidth
              type="password"
              value={pinCode}
            />
            {/* Error message */}
            <Box height={30} style={{ paddingTop: '10px' }}>
              <Typography data-testid="error-text" align="center" color={color.RED} fontSize={textSize.kiosk.SMALL}>
                {adminPinError}
              </Typography>
            </Box>
          </Grid>
        </Grid>
        <AbsoluteBox raised fullWidth bg="x-light-gray" bottom="0" left="0">
          <Grid>
            <NumPadAction
              onClick={handleKeyboardClick}
              isLoading={isLoading}
              isConfirmDisabled={!(pinCode && pinCode.length) || isLoading}
              onConfirm={onNextPageClick}
              onBack={onPrevPageClick}
              layout="row"
              confirmConfig={{
                label: nextButtonText,
                backLabel: backButtonText,
                customSize: 'x-large',
                width: 475,
                appearance: 'primary'
              }}
            />
          </Grid>
        </AbsoluteBox>
      </Grid>
    </>
  );
};

export default PromoCodeApprovalView;
